import { FormikProps } from 'formik'
import debounce from 'lodash/debounce'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { filterGeoAddressByPostalCode } from 'src/helpers/AddressHelpers'
import * as Yup from 'yup'

import { ActionButtonVariant } from 'src/components/ActionButton/styled'
import Icon, { Icons } from 'src/components/Icon'
import { SelectOption } from 'src/form/fields/FormFieldDropdownSelect'
import { BlockCategory } from 'src/forms/AdsForm/types'
import {
  AdsSearchFormProps,
  AdsSearchFormValues,
} from 'src/forms/AdsSearchForm'
import { useServiceRequest } from 'src/hooks/useServiceRequest'
import { actions, selectors } from 'src/redux'
import {
  ClassifiedCategories,
  ClassifiedSpecialties,
} from 'src/rest/types/Classified'
import { GeoAddress } from 'src/rest/types/GeoAddress'

export const useAdsSortingFiltersFormProps = ({
  categories,
  specialities,
  isOpen,
  isLoading,
  initialValues,
  onSubmit,
  submitButtonVariant,
}: {
  categories: ClassifiedCategories
  specialities: ClassifiedSpecialties
  isOpen?: boolean
  isLoading?: boolean
  initialValues?: AdsSearchFormProps['form']['formikForm']['initialValues']
  onSubmit: AdsSearchFormProps['form']['formikForm']['onSubmit']
  submitButtonVariant?: 'primary' | 'secondary'
}): AdsSearchFormProps => {
  const { t } = useTranslation()
  const formikRef = useRef<FormikProps<AdsSearchFormValues>>(null)

  const [suggestedAddresses, setSuggestedAddresses] = useState<
    GeoAddress[] | null
  >()

  const [addresses, getAddresses] = useServiceRequest(
    selectors.geo.getAddresses,
    actions.geo.getAddressesRequest,
    actions.geo.getAddressesReset
  )

  useEffect(() => {
    if (addresses.success) {
      const filteredAddresses = addresses.data?.filter((ad) =>
        filterGeoAddressByPostalCode(ad.postcode)
      )
      setSuggestedAddresses(filteredAddresses)
    }
  }, [addresses?.data, addresses.success])

  const handleLoadSuggestions = useCallback(
    (value: string) => {
      let updatedValue = value

      if (value && /^\d+$/.test(value) && value.length === 2) {
        if (value.startsWith('75')) {
          updatedValue = '75001'
        } else {
          updatedValue = value + '000'
        }
        getAddresses({ q: updatedValue, type: 'municipality' })
        formikRef?.current?.setFieldValue('localization', updatedValue)
      } else if (value && value.length >= 3) {
        getAddresses({ q: value, type: 'municipality' })
      }
    },
    [getAddresses]
  )

  const debouncedLoadSuggestions = useMemo(
    () => debounce(handleLoadSuggestions, 500),
    [handleLoadSuggestions]
  )
  const [showPeriodAndDays, setShowPeriodAndDays] = useState(false)
  const [hasPeriodFilter, setHasPeriodFilter] = useState(false)
  const [offerTypes, setOfferTypes] = useState<Array<string | number>>([])

  const offerTypeOptions = useMemo(() => {
    return (
      categories?.map((category) => ({
        label: category?.name ?? '',
        value: category?.id ?? '',
        form: (category?.form as BlockCategory) ?? '',
      })) ?? []
    )
  }, [categories])

  const hideSpecialities = useMemo(() => {
    return (
      offerTypes.length === 1 &&
      categories.some(
        (category) =>
          category?.id != null &&
          offerTypes.includes(category.id) &&
          category?.form === BlockCategory.UsedEquipment
      )
    )
  }, [offerTypes, categories])

  return useMemo(() => {
    return {
      texts: {
        offerTypeTitle: t('form_search_ads_offer_type_label'),
        specialityTitle: t('form_search_ads_speciality_label'),
        localizationTitle: t('form_search_ads_localization_label'),
        periodTitle: t('form_search_ads_period_label'),
        periodHelp: t('form_search_ads_period_help'),
        daysTitle: t('form_search_ads_days_label'),
        daysHelp: t('form_search_ads_days_help'),
        radiusTitle: t('form_search_ads_radius_label'),
        termTitle: t('form_search_ads_term_label'),
        submitButton: t('form_search_ads_submit_button_label'),
      },
      form: {
        showPeriodAndDays,
        formikForm: {
          innerRef: formikRef,
          validationSchema: Yup.object().shape({
            offerType: Yup.array().min(
              1,
              t('form_search_ads_offer_type_error')
            ),
            ...(showPeriodAndDays &&
              hasPeriodFilter && {
                startDate: Yup.string().required(t('yup_option')),
                endDate: Yup.string().required(t('yup_option')),
              }),
          }),
          initialValues: initialValues ?? {
            offerType: [],
            specialities: [],
            localization: '',
            radius: '2000',
            term: '',
            startDate: '',
            endDate: '',
            days: [],
          },
          onSubmit: (values, helpers) => {
            if (hideSpecialities) {
              formikRef.current?.setFieldValue('specialities', [])
            }

            const updatedValues = {
              ...values,
              ...(hideSpecialities && { specialities: [] }),
            }
            onSubmit?.(updatedValues, helpers)
          },
          onContextUpdate: (context: FormikProps<AdsSearchFormValues>) => {
            const _showPeriodAndDays = offerTypeOptions
              .filter((option: SelectOption) =>
                option.form?.includes(BlockCategory.Replacement)
              )
              .some((option: SelectOption) =>
                context.values?.offerType?.includes(option.value as string)
              )

            setOfferTypes(context.values?.offerType ?? [])

            setShowPeriodAndDays(_showPeriodAndDays)

            const _hasPeriodFilter =
              context.values?.startDate || context.values?.endDate

            setHasPeriodFilter(_hasPeriodFilter !== '')
          },
        },
        hideSpecialities,
        fields: {
          offersType: {
            placeholder: t('form_search_ads_offer_type_placeholder'),
            isMulti: true,
            isSearchable: false,
            leftAdornment: <Icon icon={Icons.profil} width={24} height={24} />,
            options: offerTypeOptions,
          },
          specialities: {
            placeholder: t('form_search_ads_speciality_placeholder'),
            isSearchable: false,
            isMulti: true,
            leftAdornment: (
              <Icon icon={Icons.stethoscopeSecondary} width={24} height={24} />
            ),
            options:
              specialities?.map((speciality) => ({
                label: speciality?.name ?? '',
                value: speciality?.id ?? '',
              })) ?? [],
            disabled: hideSpecialities,
          },
          localization: {
            placeholder: t('form_search_ads_localization_placeholder'),
            leftAdornment: (
              <Icon icon={Icons.markerMap} width={24} height={24} />
            ),
            suggestions: suggestedAddresses?.length
              ? suggestedAddresses?.map((address) => ({
                  label: address.label,
                  value: address.label,
                  params: address,
                }))
              : [],
            onlySuggestion: false,
            loadSuggestions: debouncedLoadSuggestions,
            onSubmit: (selectedValue: any) => {
              if (selectedValue?.params) {
                formikRef.current?.setFieldValue(
                  'lat',
                  selectedValue?.params?.lat
                )
                formikRef.current?.setFieldValue(
                  'lng',
                  selectedValue?.params?.long
                )
              }
            },
          },
          radius: {
            isSearchable: false,
            isMulti: false,
            placeholder: '',
            options: [
              {
                label: '2 km',
                value: '2000',
              },
              {
                label: '5 km',
                value: '5000',
              },
              {
                label: '10 km',
                value: '10000',
              },
              {
                label: '15 km',
                value: '15000',
              },
              {
                label: '20 km',
                value: '20000',
              },
            ],
          },
          startDate: {
            placeholder: 'jj/mm/aaaa',
          },
          endDate: {
            placeholder: 'jj/mm/aaaa',
          },
          days: {
            placeholder: t('form_search_ads_days_placeholder'),
            isMulti: true,
            isSearchable: false,
            options: [
              {
                label: t('form_search_ads_days_option_monday'),
                value: 'monday',
              },
              {
                label: t('form_search_ads_days_option_tuesday'),
                value: 'tuesday',
              },
              {
                label: t('form_search_ads_days_option_wednesday'),
                value: 'wednesday',
              },
              {
                label: t('form_search_ads_days_option_thursday'),
                value: 'thursday',
              },
              {
                label: t('form_search_ads_days_option_friday'),
                value: 'friday',
              },
              {
                label: t('form_search_ads_days_option_saturday'),
                value: 'saturday',
              },
              {
                label: t('form_search_ads_days_option_sunday'),
                value: 'sunday',
              },
            ],
          },
          term: {
            placeholder: t('form_search_ads_term_placeholder'),
            leftAdornment: <Icon icon={Icons.search} width={24} height={24} />,
          },
        },
        submitButtonVariant:
          submitButtonVariant === 'primary'
            ? ActionButtonVariant.tertiary
            : ActionButtonVariant.quaternary,
      },
      isOpen: !!isOpen,
      isLoading: !!isLoading,
      removeFilters: {
        onClick: () => {
          formikRef?.current?.setFieldValue('localization', '')
          formikRef?.current?.setFieldValue('offerType', [])
          formikRef?.current?.setFieldValue('specialities', [])
          formikRef?.current?.setFieldValue('term', [])
          formikRef?.current?.setFieldValue('startDate', '')
          formikRef?.current?.setFieldValue('endDate', '')
          formikRef?.current?.setFieldValue('days', [])
        },
        label: t('form_search_ads_remove_filters_button_label'),
        type: 'button',
      },
    }
  }, [
    t,
    initialValues,
    specialities,
    suggestedAddresses,
    debouncedLoadSuggestions,
    submitButtonVariant,
    isOpen,
    isLoading,
    onSubmit,
    offerTypeOptions,
    showPeriodAndDays,
    hasPeriodFilter,
    hideSpecialities,
  ])
}
